// 1. Imports React
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// 2. Imports externes
import { useDebounce } from "use-debounce";
import {
  Building2,
  CircleCheckBig,
  CircleSlash2,
  Edit,
  Eye,
  Loader2,
  MoreHorizontal,
  Plus,
  Search,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";

// 3. Imports composants UI
import { CompanyFormDialog } from "@/components/forms/CompanyFormDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { PaginationControls } from "@/components/ui/pagination-controls";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { showErrorToast, showSuccessToast } from "@/components/ui/toast";

// 4. Imports hooks personnalisés
import { useAuth } from "@/contexts/AuthContext";
import { useCompanies, useUpdateCompany } from "@/hooks/useCompanies";

// 5. Imports types
import type { Company } from "@/types/company.type";

// 6. Composant
export const CompaniesPage = () => {
  // ========== A. Hooks React ==========
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(2);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive"
  >("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  // Debounce pour la recherche
  const [debouncedSearchTerm] = useDebounce(searchTerm, 1000);

  // ========== B. Custom Hooks ==========
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSuperuser = user?.role === "SUPERUSER";

  // Réinitialiser la page quand les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, debouncedSearchTerm]);

  // Récupérer les entreprises
  const {
    data: companiesData,
    isLoading,
    isFetching,
    error,
  } = useCompanies(
    {
      page: currentPage,
      limit: itemsPerPage,
      search: debouncedSearchTerm,
      isActive: statusFilter === "all" ? undefined : statusFilter === "active",
    },
    { enabled: isSuperuser }
  );

  const companies = companiesData?.companies || [];
  const totalPages = companiesData?.pagination.totalPages || 0;
  const totalItems = companiesData?.pagination.total || 0;

  const { mutate: updateCompanyMutation } = useUpdateCompany();

  // ========== C. Handlers ==========
  // Créer une nouvelle entreprise
  const handleCreateCompany = () => {
    setDialogOpen(true);
    setSelectedCompany(null);
  };

  // Modifier une entreprise
  const handleEditCompany = (company: Company) => {
    setDialogOpen(true);
    setSelectedCompany(company);
  };

  // Basculer le statut actif/inactif d'une entreprise
  const handleToggleCompanyStatus = async (company: Company) => {
    updateCompanyMutation(
      {
        id: company.id,
        data: { isActive: !company.isActive },
      },
      {
        onSuccess: () => {
          showSuccessToast(
            company.isActive
              ? "Entreprise désactivée avec succès"
              : "Entreprise activée avec succès"
          );
        },
        onError: (error: Error) => {
          showErrorToast(error.message || "Erreur lors de la modification");
        },
      }
    );
  };

  // Voir les détails d'une entreprise
  const handleViewCompanyDetails = (id: string) => {
    navigate(`/superuser/companies/${id}`);
  };

  // Fermer le dialog après succès
  const handleSuccess = () => {
    setDialogOpen(false);
    setSelectedCompany(null);
  };

  // ========== D. Gestion des erreurs ==========
  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">Entreprises</h1>
        <div className="flex items-center justify-center h-64">
          <p className="text-red-600">
            {error?.message || "Erreur lors du chargement des entreprises"}
          </p>
        </div>
      </div>
    );
  }

  // ========== E. JSX ==========
  return (
    <div className="space-y-6">
      {/* En-tête avec bouton Créer */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
            <Building2 className="h-8 w-8 text-primary" />
            Gestion des Entreprises
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            {totalItems} entreprise{totalItems > 1 ? "s" : ""} au total
          </p>
        </div>
        <Button
          onClick={handleCreateCompany}
          className="gap-2 w-full sm:w-auto"
        >
          <Plus className="h-4 w-4" />
          <span className="hidden sm:inline">Nouvelle entreprise</span>
          <span className="sm:hidden">Nouvelle</span>
        </Button>
      </div>

      {/* Card avec filtres et tableau */}
      <Card className="p-4 sm:p-6">
        {/* Filtres */}
        <div className="mb-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
          {/* Recherche */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Rechercher par nom ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
            {isFetching && !isLoading && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
              </div>
            )}
          </div>
          {/* Filtre par statut */}
          <Select
            value={statusFilter}
            onValueChange={(value: "all" | "active" | "inactive") =>
              setStatusFilter(value)
            }
          >
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="active">Actives</SelectItem>
              <SelectItem value="inactive">Inactives</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Vue mobile/tablette : Cartes */}
        {isLoading && !companiesData ? (
          <div className="space-y-4 lg:hidden">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="p-4">
                <Skeleton className="h-20 w-full" />
              </Card>
            ))}
          </div>
        ) : companies.length === 0 ? (
          <div className="lg:hidden text-center py-8">
            <div className="flex flex-col items-center gap-2">
              <Building2 className="h-12 w-12 text-muted-foreground" />
              <div className="text-center">
                <h3 className="text-lg font-medium text-muted-foreground">
                  {debouncedSearchTerm || statusFilter !== "all"
                    ? "Aucune entreprise trouvée"
                    : "Aucune entreprise"}
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {debouncedSearchTerm || statusFilter !== "all"
                    ? "Essayez de modifier vos critères de recherche"
                    : "Commencez par créer votre première entreprise"}
                </p>
                {!debouncedSearchTerm && statusFilter === "all" && (
                  <Button
                    onClick={handleCreateCompany}
                    className="mt-4"
                    size="sm"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Créer une entreprise
                  </Button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4 lg:hidden">
            {companies.map((company) => (
              <Card
                key={company.id}
                className="p-4 cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => handleViewCompanyDetails(company.id)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
                      <Building2 className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-base truncate">
                        {company.name}
                      </h3>
                      <p className="text-sm text-muted-foreground truncate mt-1">
                        {company.email}
                      </p>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <Badge
                          variant={company.isActive ? "default" : "secondary"}
                          className={
                            company.isActive
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }
                        >
                          <div className="flex items-center gap-1">
                            {company.isActive ? (
                              <CircleCheckBig className="h-3 w-3" />
                            ) : (
                              <CircleSlash2 className="h-3 w-3" />
                            )}
                            <span>
                              {company.isActive ? "Active" : "Inactive"}
                            </span>
                          </div>
                        </Badge>
                        {company.siret && (
                          <span className="text-xs text-muted-foreground font-mono">
                            SIRET: {company.siret}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        Créée le{" "}
                        {new Date(company.createdAt).toLocaleDateString(
                          "fr-FR"
                        )}
                      </p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewCompanyDetails(company.id);
                        }}
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        Voir détails
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditCompany(company);
                        }}
                      >
                        <Edit className="mr-2 h-4 w-4" />
                        Modifier
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleCompanyStatus(company);
                        }}
                      >
                        {company.isActive ? (
                          <>
                            <ToggleLeft className="mr-2 h-4 w-4" />
                            Désactiver
                          </>
                        ) : (
                          <>
                            <ToggleRight className="mr-2 h-4 w-4" />
                            Activer
                          </>
                        )}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Vue desktop : Tableau */}
        <div className="hidden lg:block rounded-lg border">
          <Table>
            <TableHeader className="bg-muted/50">
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>SIRET</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Date création</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>

            {isLoading && !companiesData ? (
              <TableBody>
                {[1, 2, 3].map((i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Skeleton className="h-4 w-32" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-40" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-5 w-20 rounded-full" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-28" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton className="ml-auto h-8 w-8 rounded-md" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            ) : companies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  <div className="flex flex-col items-center gap-2">
                    <Building2 className="h-12 w-12 text-muted-foreground" />
                    <div className="text-center">
                      <h3 className="text-lg font-medium text-muted-foreground">
                        {debouncedSearchTerm || statusFilter !== "all"
                          ? "Aucune entreprise trouvée"
                          : "Aucune entreprise"}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {debouncedSearchTerm || statusFilter !== "all"
                          ? "Essayez de modifier vos critères de recherche"
                          : "Commencez par créer votre première entreprise"}
                      </p>
                      {!debouncedSearchTerm && statusFilter === "all" && (
                        <Button
                          onClick={handleCreateCompany}
                          className="mt-4"
                          size="sm"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Créer une entreprise
                        </Button>
                      )}
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              <TableBody>
                {companies.map((company) => (
                  <TableRow key={company.id} className="hover:bg-primary/10">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                          <Building2 className="h-5 w-5" />
                        </div>
                        <span
                          className="font-medium cursor-pointer hover:underline"
                          onClick={() => handleViewCompanyDetails(company.id)}
                        >
                          {company.name}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>{company.email}</TableCell>
                    <TableCell className="font-mono text-sm">
                      {company.siret || "-"}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={company.isActive ? "default" : "secondary"}
                        className={
                          company.isActive
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }
                      >
                        <div className="flex items-center gap-1">
                          {company.isActive ? (
                            <CircleCheckBig className="h-3 w-3" />
                          ) : (
                            <CircleSlash2 className="h-3 w-3" />
                          )}
                          <span>
                            {company.isActive ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(company.createdAt).toLocaleDateString("fr-FR")}
                    </TableCell>
                    <TableCell className="text-center">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => handleViewCompanyDetails(company.id)}
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            Voir détails
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleEditCompany(company)}
                          >
                            <Edit className="mr-2 h-4 w-4" />
                            Modifier
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleToggleCompanyStatus(company)}
                          >
                            {company.isActive ? (
                              <>
                                <ToggleLeft className="mr-2 h-4 w-4" />
                                Désactiver
                              </>
                            ) : (
                              <>
                                <ToggleRight className="mr-2 h-4 w-4" />
                                Activer
                              </>
                            )}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
          {/* Pagination */}
          <PaginationControls
            page={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onPageSizeChange={(newSize) => {
              setItemsPerPage(newSize);
              setCurrentPage(1);
            }}
          />
        </div>
      </Card>

      {/* Dialog pour créer/modifier une entreprise */}
      <CompanyFormDialog
        open={dialogOpen}
        company={selectedCompany}
        onSuccess={handleSuccess}
        onOpenChange={setDialogOpen}
      />
    </div>
  );
};
